// traceability: requirements: [FR-002, FR-038]
import { createContext, useContext, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getMeApi, logoutApi } from './api';
import type { User, AuthTokens } from '@/types/models';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    login,
    logout: storeLogout,
    setUser,
    setLoading,
  } = useAuthStore();

  const hydrateUser = useCallback(async () => {
    if (!tokens?.accessToken) {
      setLoading(false);
      return;
    }
    try {
      const me = await getMeApi();
      setUser(me);
    } catch {
      storeLogout();
    } finally {
      setLoading(false);
    }
  }, [tokens?.accessToken, setUser, storeLogout, setLoading]);

  useEffect(() => {
    void hydrateUser();
  }, [hydrateUser]);

  const handleLogout = useCallback(async () => {
    try {
      await logoutApi();
    } finally {
      storeLogout();
    }
  }, [storeLogout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
